import { Controller } from 'stimulus'

// Presentation only: the same metadata controls and save session serve both layouts.
export default class extends Controller {
  static targets = ['toggle', 'menu', 'title', 'save']

  connect() {
    this.compact = window.matchMedia('(max-width: 1399px)')
    this.onResize = () => {
      const focusInMenu = this.menuTarget.contains(document.activeElement)
      this.close(false)
      if (this.compact.matches && focusInMenu) this.toggleTarget.focus({ preventScroll: true })
    }
    this.compact.addEventListener('change', this.onResize)
    this.onInput = event => {
      if (event.target.matches('[name="post[title]"]')) this.updateTitle()
    }
    this.onClick = event => {
      if (!this.menuTarget.contains(event.target) && !this.toggleTarget.contains(event.target) ||
          event.target.closest('[data-document-export], [data-document-save-details]')) this.close(false)
    }
    this.onKeydown = event => {
      if (event.key === 'Escape' && this.compact.matches && this.isOpen) {
        event.preventDefault()
        event.stopImmediatePropagation()
        this.close()
      } else if (['Enter', ' '].includes(event.key) && event.target.matches('[data-chrome-keyboard]')) {
        event.preventDefault()
        event.target.click()
      }
    }
    this.onFocus = event => {
      this.lastMenuFocus = this.menuTarget.contains(event.target) ? event.target : null
      if (this.isOpen && !this.menuTarget.contains(event.target) && event.target !== this.toggleTarget) this.close(false)
    }
    this.onCache = () => this.close(false)
    this.element.addEventListener('input', this.onInput)
    document.addEventListener('click', this.onClick)
    document.addEventListener('keydown', this.onKeydown, true)
    document.addEventListener('focusin', this.onFocus)
    document.addEventListener('turbo:before-cache', this.onCache)
    // Covers initial connection order, save state updates, and Turbo status replacement.
    this.observer = new MutationObserver(() => { this.updateSave(); this.prepareControls(); this.restoreChoiceFocus() })
    this.observer.observe(this.element.querySelector('.editor-header'), { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'data-state'] })
    this.updateTitle()
    this.updateSave()
    this.prepareControls()
  }

  get isOpen() { return this.menuTarget.classList.contains('is-open') }

  toggle() {
    if (this.isOpen) return this.close()
    this.menuTarget.classList.add('is-open')
    this.toggleTarget.setAttribute('aria-expanded', 'true')
  }

  close(restoreFocus = true) {
    if (!this.isOpen) return
    this.menuTarget.classList.remove('is-open')
    this.toggleTarget.setAttribute('aria-expanded', 'false')
    this.menuTarget.querySelectorAll('.options').forEach(menu => {
      menu.classList.add('d-none')
      menu.classList.remove('d-block')
    })
    if (restoreFocus !== false) this.toggleTarget.focus({ preventScroll: true })
  }

  updateTitle() {
    const title = this.element.querySelector('[name="post[title]"]')?.value.trim() || 'Untitled document'
    this.titleTarget.textContent = title
    this.toggleTarget.title = `${title} — document menu`
  }

  updateSave() {
    const summary = this.element.querySelector('[data-save-summary]')
    if (!summary) return
    if (this.saveTarget.textContent !== summary.textContent) this.saveTarget.textContent = summary.textContent
    if (this.saveTarget.dataset.state !== summary.dataset.state) this.saveTarget.dataset.state = summary.dataset.state
    const saved = summary.dataset.state === 'saved'
    const status = summary.getAttribute('aria-label') || summary.textContent
    this.saveTarget.title = summary.textContent
    // On a phone this indicator is the only save control, so it stays operable
    // in every state - including "saved" - and names the status it opens.
    this.saveTarget.setAttribute('aria-label', saved ? 'All changes saved. View document save status' : status)
    this.saveTarget.removeAttribute('aria-disabled')
    this.saveTarget.tabIndex = 0
    if (!this.saveTarget.hasAttribute('aria-haspopup')) this.saveTarget.setAttribute('aria-haspopup', 'dialog')
  }

  showSaveStatus() {
    const delivery = document.getElementById('publishForm')?._documentDelivery
    if (delivery) delivery.ui.openStatus(this.saveTarget)
  }

  prepareControls() {
    const controls = this.element.querySelectorAll('.editor-header .menu-item[data-element-id], #editorDocumentMenu .project-option, #editorDocumentMenu .format-button, #editorDocumentMenu .template-option, #editorDocumentMenu .status-button, #editorDocumentMenu #unscheduleMenuButton, .editor-header-left .nav-toggle, #editorSidebarOpenButton')
    controls.forEach(control => {
      control.tabIndex = 0
      control.setAttribute('role', 'button')
      control.dataset.chromeKeyboard = ''
      if (control.matches('.nav-toggle')) control.setAttribute('aria-label', 'Open navigation')
      if (control.id === 'editorSidebarOpenButton') control.setAttribute('aria-label', 'Open side panel')
      const options = document.getElementById(control.dataset.elementId)
      if (options?.classList.contains('options')) {
        control.setAttribute('aria-controls', options.id)
        control.setAttribute('aria-expanded', String(!options.classList.contains('d-none')))
      }
    })
  }

  restoreChoiceFocus() {
    if (this.compact.matches && !this.isOpen) return
    const focused = document.activeElement === document.body ? this.lastMenuFocus : document.activeElement
    if (!focused) return
    if (!this.menuTarget.contains(focused)) return
    const closedMenu = focused.closest('.options.d-none')
    if (!closedMenu) return
    const trigger = Array.from(this.menuTarget.querySelectorAll('.menu-item[aria-controls]'))
      .find(control => control.getAttribute('aria-controls') === closedMenu.id)
    trigger?.focus({ preventScroll: true })
  }

  disconnect() {
    this.close(false)
    this.observer.disconnect()
    this.compact.removeEventListener('change', this.onResize)
    this.element.removeEventListener('input', this.onInput)
    document.removeEventListener('click', this.onClick)
    document.removeEventListener('keydown', this.onKeydown, true)
    document.removeEventListener('focusin', this.onFocus)
    document.removeEventListener('turbo:before-cache', this.onCache)
  }
}
